/**
 * Pasting a block of figures into the revenue entry grid.
 *
 * **Positional, from the focused cell** — values fill right and down from
 * wherever the caret is, like a spreadsheet. Nothing is inferred from the
 * clipboard's content. (Nilay's call, 8 Sep: keep it dumb.)
 *
 * ⚠️ **The one thing to know when using it.** The grid orders columns
 * English-first by `sort_order`; the source workbook orders them its own way,
 * and the two agree for only the first three:
 *
 * ```
 *   position   grid          workbook
 *   1-3        Phony Texts · Tiny Texts · Deadzone   (agree)
 *   4          Arabic        PT France
 *   5          PT France     PT German
 *   6          Spanish       PT Dutch
 *   7          TT France     Spanish
 *   8          PT German     Arabic
 *   9          PT Dutch      Norwegian
 *   10         TT German     TT France
 *   11         TT Dutch      TT German
 *   12         Norwegian     TT Dutch
 * ```
 *
 * So pasting a whole row straight out of the workbook puts most figures on the
 * wrong channel, and every one of them looks plausible. Paste column by column,
 * or check the row before saving. Nothing is written until Save, and the change
 * record captures whatever is.
 *
 * Three rules it does keep, because each is a semantic rather than a feature:
 *
 * · **A blank cell clears; it never writes zero.** A pasted blank means *not
 *   recorded*, exactly as an emptied input does — the load-bearing distinction
 *   on this page.
 * · **Nothing is applied partially.** One unparseable cell rejects the whole
 *   paste. Half a revenue block is worse than none, because nothing on screen
 *   tells you which half landed.
 * · **It stops at the grid's edges** rather than wrapping.
 */

import { sanitizeMoneyInput } from './money'

export interface PasteTarget {
  /** Grid columns left to right — only the profile id is needed. */
  profileIds: string[]
  /** Grid rows as ISO dates, top to bottom. */
  days: string[]
  /** Where the caret was: values fill right and down from here. */
  anchor: { row: number; col: number }
}

export interface PasteResult {
  /** `profileId|day` → the value to stage. `''` clears the cell. */
  edits: Record<string, string>
  applied: number
  cleared: number
  /** Cells that fell off the right or bottom edge, and were ignored. */
  ignored: number
  /** Set when nothing was applied; the message is shown as-is. */
  error?: string
}

/**
 * One line into cells, respecting double quotes.
 *
 * Quote handling is not optional here: a CSV writes a thousands separator as
 * `"$1,264.50"`, and splitting naively on the comma turns one figure into `1`
 * and `264.50` — two plausible numbers from one real one. Found by this
 * module's own test.
 *
 * An **unquoted** comma stays a delimiter, which is correct CSV: `1,264.50` is
 * two fields. Only the quotes make it literal.
 */
function splitLine(line: string, delimiter: string): string[] {
  const cells: string[] = []
  let cell = ''
  let quoted = false
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i]
    if (char === '"') {
      // A doubled quote inside a quoted field is one literal quote.
      if (quoted && line[i + 1] === '"') {
        cell += '"'
        i += 1
      } else {
        quoted = !quoted
      }
      continue
    }
    if (char === delimiter && !quoted) {
      cells.push(cell)
      cell = ''
      continue
    }
    cell += char
  }
  cells.push(cell)
  return cells.map((value) => value.trim())
}

/** Tab if the clipboard has one — that is what a spreadsheet copy carries. */
function toCells(text: string): string[][] {
  const lines = text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((line) => line.trim() !== '')
  const delimiter = lines[0]?.includes('\t') ? '\t' : ','
  return lines.map((line) => splitLine(line, delimiter))
}

export function parsePaste(text: string, target: PasteTarget): PasteResult {
  const nothing: PasteResult = { edits: {}, applied: 0, cleared: 0, ignored: 0 }
  const rows = toCells(text)
  if (rows.length === 0) return { ...nothing, error: 'Nothing to Paste.' }

  const edits: Record<string, string> = {}
  let applied = 0
  let cleared = 0
  let ignored = 0

  for (let r = 0; r < rows.length; r += 1) {
    const day = target.days[target.anchor.row + r]
    if (!day) {
      ignored += rows[r]?.length ?? 0
      continue
    }
    const row = rows[r] ?? []
    for (let c = 0; c < row.length; c += 1) {
      const profileId = target.profileIds[target.anchor.col + c]
      if (!profileId) {
        ignored += 1
        continue
      }
      const raw = row[c] ?? ''
      if (raw.trim() === '') {
        edits[`${profileId}|${day}`] = ''
        cleared += 1
        continue
      }
      const value = sanitizeMoneyInput(raw, '')
      if (value === '') {
        // Reject the block rather than apply part of it.
        return {
          ...nothing,
          error: `“${raw}” Is Not a Number, so Nothing Was Pasted.`,
        }
      }
      edits[`${profileId}|${day}`] = value
      applied += 1
    }
  }

  if (applied === 0 && cleared === 0) {
    return { ...nothing, error: 'Nothing Landed on the Grid. Check the Selected Cell.' }
  }
  return { edits, applied, cleared, ignored }
}
